import { useStytchB2BClient } from '@stytch/react/b2b'
import {
  B2BOrganizationsMembersSearchOptions,
  B2BOrganizationsMembersSearchResponse,
} from '@stytch/vanilla-js'
import { useQuery, UseQueryOptions } from '@tanstack/react-query'
import useMutation, {
  UseMutationOptions,
} from '@/service-library/core-hooks/useMutation'
import { getRedirectUrl } from '@/utils/auth'

type SearchMembersParams = {
  filterQuery?: B2BOrganizationsMembersSearchOptions['query']
}

export function useSearchStytchMembers({
  filterQuery,
  ...options
}: SearchMembersParams &
  Omit<
    UseQueryOptions<B2BOrganizationsMembersSearchResponse>,
    'queryKey'
  > = {}) {
  const stytch = useStytchB2BClient()
  const queryKey = ['stytch-members', { query: filterQuery }]
  const query = useQuery({
    queryKey,
    queryFn: () =>
      stytch.organization.members
        .search({
          query: filterQuery,
          limit: 1000, // 1000 is the max limit allowed by Stytch. If we have more than that, we need to implement pagination
        })
        .then((data) => data),
    ...options,
  })

  return { ...query, members: query.data?.members, queryKey }
}

type InviteMemberParams = {
  email_address: string
}

export function useInviteMember(
  options?: UseMutationOptions<void, unknown, InviteMemberParams>,
) {
  const stytch = useStytchB2BClient()

  const mutation = useMutation({
    mutationFn: async ({ email_address }: InviteMemberParams) => {
      await stytch.magicLinks.email.invite({
        email_address,
        invite_redirect_url: getRedirectUrl(),
      })
    },
    ...options,
  })

  return {
    ...mutation,
    inviteMember: mutation.mutateAsync,
  }
}
